import { HttpContext, HttpHeaders } from '@angular/common/http';
import { inject, Injectable, NgZone } from '@angular/core';
import { INCLUDE_AUTH_TOKEN, JwtService } from '@okcargo-apps/services';
import { NotificationResponse, NotificationType } from '@okcargo/query-processor';
import { SseClient } from 'ngx-sse-client';
import { ServerEventBusService } from '../domain/server-event-bus.service';
import { IServerEvent, IServerEventType } from '../domain/server-event.model';

@Injectable({
  providedIn: 'root',
})
export class ServerEventListenerService {
  private readonly jwtService = inject(JwtService);
  private readonly sseClient = inject(SseClient);
  private readonly zone = inject(NgZone);
  private readonly serverEventBus = inject(ServerEventBusService);

  public getServerSentEvent<T>(url: string): void {
    this.connect(url);
  }

  private connect<T>(url: string): void {
    if (this.jwtService.accessToken) {
      // console.log('[ServerEventListenerService] connect() url', url);

      const headers = new HttpHeaders().set('Accept', 'text/event-stream');
      const context = new HttpContext().set(INCLUDE_AUTH_TOKEN, true);

      this.zone.runOutsideAngular(() => {
        this.sseClient.stream(url, { responseType: 'event' }, { context, headers }).subscribe((event) => {
          // console.log('[ServerEventListenerService] connect() this.sseClient.stream() event', event);
          if (event.type === 'error') {
            const errorEvent = event as ErrorEvent;
            console.error(errorEvent.error, errorEvent.message);
          } else {
            const messageEvent = event as MessageEvent;

            this.zone.run(() => {
              try {
                const serverEventData = JSON.parse(messageEvent.data);
                const serverEvent = this.mapToServerEvent(serverEventData);
                this.serverEventBus.addServerEvent(serverEvent);
              } catch (error) {
                console.error('Error parsing SSE data:', error, messageEvent.data);
              }
            });
          }
        });
      });
    }
  }

  private mapToServerEvent(data: NotificationResponse): IServerEvent {
    return {
      aggregateId: data.aggregateId,
      aggregateName: data.aggregateName,
      modifiedAt: new Date(data.modifiedAt),
      serverEventType: this.mapToIServerEventType(data.notificationType),
      processedBy: [],
    };
  }

  private mapToIServerEventType(type: NotificationType): IServerEventType {
    switch (type) {
      case NotificationType.CREATED:
        return IServerEventType.CREATED;
      case NotificationType.MODIFIED:
        return IServerEventType.MODIFIED;
      case NotificationType.BATCH_PROCESSED:
        return IServerEventType.BATCH_PROCESSED;
      default:
        return IServerEventType.BATCH_PROCESSED;
    }
  }
}
